/**
 * PeriodPicker — выбор периода-диапазона: шапка (подпись + диапазон · N дней),
 * пресеты и календарь с выделением диапазона. Десктоп — правый дравер, мобилка
 * — bottom-sheet. Open-управляемый; наружу отдаёт готовый диапазон.
 */

import {useEffect, useState} from 'react';
import {X} from 'lucide-react';

import {SheetGrabber} from '@components/SheetGrabber';
import {Button} from '@components/ui/Button';
import {Sheet, SheetContent} from '@components/ui/Sheet';
import {cn} from '@lib/utils';

import {DEFAULT_RANGE, matchPreset, PERIOD_PRESETS, type Range, rangeLabelFull} from './periods';
import {RangeCalendar} from './RangeCalendar';

type PeriodPickerProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  value: Range | null;
  onApply: (range: Range) => void;
  mobile?: boolean;
};

export const PeriodPicker = ({
  open,
  onOpenChange,
  value,
  onApply,
  mobile = false,
}: PeriodPickerProps) => {
  const [draft, setDraft] = useState<Range>(value ?? DEFAULT_RANGE());
  const [phase, setPhase] = useState<0 | 1>(0);

  useEffect(() => {
    if (open) {
      setDraft(value ?? DEFAULT_RANGE());
      setPhase(0);
    }
  }, [open, value]);

  const pickDay = (iso: string) => {
    if (phase === 0) {
      setDraft({from: iso, to: iso});
      setPhase(1);
    } else if (iso >= draft.from) {
      setDraft({from: draft.from, to: iso});
      setPhase(0);
    } else {
      setDraft({from: iso, to: iso});
    }
  };

  const active = matchPreset(draft);
  const body = (
    <>
      {mobile && <SheetGrabber />}
      <div className="mb-4 flex items-start justify-between gap-3">
        <div>
          <div className="text-[12.5px] text-muted-foreground">Период отчёта</div>
          <div className="mt-0.5 text-[19px] font-bold -tracking-[.01em]">
            {rangeLabelFull(draft)}
          </div>
        </div>
        <button
          onClick={() => onOpenChange(false)}
          aria-label="Закрыть"
          className="grid size-8 shrink-0 place-items-center rounded-lg bg-secondary text-muted-foreground"
        >
          <X className="size-4" strokeWidth={2} />
        </button>
      </div>

      <div className="mb-4 flex flex-wrap gap-2">
        {PERIOD_PRESETS.map((p) => (
          <button
            key={p.key}
            onClick={() => {
              setDraft(p.range());
              setPhase(0);
            }}
            className={cn(
              'h-[34px] rounded-[10px] px-[13px] text-[13px] font-medium transition-colors',
              active === p.key
                ? 'bg-skr-dark text-white'
                : 'bg-secondary text-skr-ink hover:bg-muted',
            )}
          >
            {p.label}
          </button>
        ))}
      </div>

      <RangeCalendar from={draft.from} to={draft.to} onPickDay={pickDay} />

      <Button
        className="mt-4 h-[50px] w-full rounded-[13px] text-[15px]"
        onClick={() => {
          onApply(draft);
          onOpenChange(false);
        }}
      >
        Применить период
      </Button>
    </>
  );

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side={mobile ? 'bottom' : 'right'}
        showClose={false}
        className={
          mobile
            ? 'gap-0 rounded-t-[22px] p-5 pb-6'
            : 'flex w-[380px] max-w-[94vw] flex-col gap-0 overflow-y-auto bg-card p-6'
        }
      >
        {body}
      </SheetContent>
    </Sheet>
  );
};
